'use client';

import clsx from 'clsx';
import Image from 'next/image';

import { useLocale, useTranslations } from 'next-intl';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Keyboard, Mousewheel, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Checkbox } from '../Checkbox/Checkbox';
import ElasticSlider from '../ElasticSlider/ElasticSlider';
import { Icon } from '../Icon/Icon';
import { Typography } from '../Typography/Typography';

import { InputFormClient } from '@/components/storyblok-components/Contact/components/Inputs/InputForm/InputFormClient';
import { InputPhoneClient } from '@/components/storyblok-components/Contact/components/Inputs/InputPhone/InputPhoneClient';
import { Accordion } from '@/components/ui/Accordion/Accordion';
import ShinyText from '@/components/ui/ShinyText/ShinyText';
import { CurrencySwitcher } from '@/layout/Layout/components/Header/components/CurrencySwitcher/CurrencySwitcher';
import { useCurrency } from '@/providers/CurrencyProvider';
import { LocationStoryblok } from '@/types/storyblok/storyblok-types';
import { ConfiguratorSimulation } from './ConfiguratorSimulation/ConfiguratorSimulation';

import 'swiper/css';
import 'swiper/css/pagination';
import styles from './Configurator.module.scss';

type SectionProps = {
    locations: LocationStoryblok[];
    className?: string;
};

export const Configurator = ({ className, locations }: SectionProps) => {
    const t = useTranslations('Configurator');
    const [selectedStep, setSelectedStep] = useState(1);
    const [activeUids, setActiveUids] = useState<string[]>([]);
    const [allowOffPlan, setAllowOffPlan] = useState(true);
    const [stepError, setStepError] = useState<string | null>(null);
    const { currency, getExchangeRate } = useCurrency();
    const [currentExchange, setCurrentExchange] = useState<number | null>(null);
    const [amountDefaultCurrency, setAmountDefaultCurrency] = useState<number | null>(null);
    const [counterValue, setCounterValue] = useState(5);
    const locale = useLocale();

    const { control, getValues, trigger, setValue, reset, watch } = useForm({
        defaultValues: {
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            company: '',
            investmentAmount: 0,
        },
    });

    const investmentAmount = watch('investmentAmount');
    const triggerValidation = async () => {
        const isValid = await trigger(['firstname', 'lastname', 'email', 'phone']);

        if (isValid) {
            return true;
        }
    };

    const submitForm = async () => {
        const isValid = await triggerValidation();
        if (!isValid) return;
        const data = getValues();

        const { firstname, lastname, phone, company, email } = data;
        const payload = {
            firstname,
            surname: lastname,
            phone,
            company,
            email,
            investmentAmount,
            language: locale,
            currency,
            locations: JSON.stringify(activeUids),

            marketingConsent: true,
        };
        const response = await fetch('/api/configurator/', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        if (response.status !== 200) {
            setStepError(response.statusText);
        } else {
            setSelectedStep(4);
            setStepError('');
        }
    };

    const orderedLocations = useMemo(
        () =>
            locations.map(location => ({
                location: location.content,
                city: location.content.city,
            })),
        [locations],
    );
    const [groupedLocations, setGroupedLocations] = useState<Record<string, typeof orderedLocations>>({});
    useEffect(() => {
        let isMounted = true;

        async function buildGroupedLocations() {
            const sortedLocations = orderedLocations.slice().sort((a, b) => b.city.name.localeCompare(a.city.name));

            const results = await Promise.all(
                sortedLocations.map(async loc => {
                    const cityName = loc.city.name;
                    const baseCurrency: string = loc.city.content?.currency || 'USD';

                    // getExchangeRate returns a Promise, so await it here
                    const rate = await getExchangeRate(baseCurrency, 'USD');

                    // Build updated unit types
                    const updatedTypes = loc.location.types.map(type => {
                        const sPriceNum = parseFloat(type.startingPrice || '0');
                        const ePriceNum = parseFloat(type.endingPrice || '0');

                        return {
                            ...type,
                            startingPriceUsd: sPriceNum * rate,
                            endingPriceUsd: ePriceNum * rate,
                        };
                    });

                    return {
                        cityName,
                        updatedLocation: {
                            ...loc,
                            location: {
                                ...loc.location,
                                types: updatedTypes,
                            },
                        },
                    };
                }),
            );

            const tempGroups: Record<string, typeof orderedLocations> = {};

            for (const { cityName, updatedLocation } of results) {
                if (!tempGroups[cityName]) {
                    tempGroups[cityName] = [];
                }
                tempGroups[cityName].push(updatedLocation);
            }

            // Only set state if component is still mounted
            if (isMounted) {
                setGroupedLocations(tempGroups);
            }
        }

        buildGroupedLocations();

        return () => {
            isMounted = false;
        };
    }, [orderedLocations, getExchangeRate]);

    useEffect(() => {
        async function getRate() {
            if (currency === 'USD') {
                setCurrentExchange(1);
                return setValue('investmentAmount', amountDefaultCurrency);
            }
            const rate = await getExchangeRate('USD', currency);
            setCurrentExchange(rate);
            setValue('investmentAmount', amountDefaultCurrency * rate);
        }

        getRate();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    // PRICES RANGES
    const { smallestStartingPrice, largestEndingPrice } = useMemo(() => {
        // 1) Flatten + filter all location objects that match activeUid
        let relevantTypes: Array<any> = [];

        // eslint-disable-next-line unused-imports/no-unused-vars
        Object.entries(groupedLocations).forEach(([cityName, locArr]) => {
            locArr
                .filter(locObj => activeUids.includes(locObj.location.name))
                .forEach(locObj => {
                    // Only push if there are actually some types
                    if (locObj.location.types) {
                        relevantTypes = relevantTypes.concat(locObj.location.types);
                    }
                });
        });

        // 2) If no relevant types, return null for both
        if (relevantTypes.length === 0) {
            return { smallestStartingPrice: null, largestEndingPrice: null };
        }

        // 3) Compute global min and max (in USD) with off-plan discount if allowOffPlan
        let minPrice = Infinity;
        let maxPrice = -Infinity;

        relevantTypes.forEach(type => {
            const discountPercent = parseFloat(type.discountOffPlanPercentage) || 0;

            // Starting Price in USD
            let startNum = parseFloat(type.startingPriceUsd) || 0;
            if (allowOffPlan) {
                startNum *= 1 - discountPercent / 100;
            }
            if (startNum < minPrice) {
                minPrice = startNum;
            }

            // Ending Price in USD
            let endNum = parseFloat(type.endingPriceUsd) || 0;
            if (allowOffPlan) {
                endNum *= 1 - discountPercent / 100;
            }
            if (endNum > maxPrice) {
                maxPrice = endNum;
            }
        });

        // 4) If NOT USD, multiply final min and max by currentExchange
        //    (assuming currentExchange = rate from USD -> chosen currency)
        if (currency !== 'USD' && currentExchange) {
            minPrice *= currentExchange;
            maxPrice *= currentExchange;
        }

        return {
            smallestStartingPrice: Number.isFinite(minPrice) ? minPrice : null,
            largestEndingPrice: Number.isFinite(maxPrice) ? maxPrice : null,
        };
    }, [groupedLocations, activeUids, allowOffPlan, currency, currentExchange]);
    // MATCHES
    const groupedLocationsResults = useMemo(() => {
        // will mirror groupedLocations but after filtering
        const results: Record<string, typeof orderedLocations> = {};

        if (amountDefaultCurrency == null) return results;

        Object.entries(groupedLocations).forEach(([cityName, locArr]) => {
            const validLocations = locArr
                .filter(locObj => activeUids.includes(locObj.location.name))
                .map(locObj => {
                    const matchedTypes: typeof locObj.location.types = [];

                    locObj.location.types.forEach(type => {
                        const startUsd = parseFloat(type.startingPriceUsd) || 0;

                        /* —— keep base copy when startUsd < amount —— */
                        const keepBase = startUsd < amountDefaultCurrency;

                        /* —— discounted branch —— */
                        let keepDisc = false;
                        let discStart = 0;
                        let discEnd = 0; // optional, for display
                        const discPct = parseFloat(type.discountOffPlanPercentage) || 0;

                        if (allowOffPlan && discPct > 0) {
                            // user spec: multiply by (discPct / 100)
                            discStart = startUsd * (1 - discPct / 100);
                            discEnd = parseFloat(type.endingPriceUsd) * (1 - discPct / 100);
                            keepDisc = discStart < amountDefaultCurrency;
                        }

                        /* —— push logic —— */
                        if (keepBase) {
                            // regular copy
                            matchedTypes.push({
                                ...type,
                                isOffPlan: false,
                            });
                        }

                        if (keepDisc) {
                            // discounted copy
                            matchedTypes.push({
                                ...type,
                                startingPriceUsd: discStart,
                                endingPriceUsd: discEnd,
                                isOffPlan: true,
                            });
                        }
                    });
                    // discard locations with zero matching types
                    if (matchedTypes.length === 0) return null;

                    return {
                        ...locObj,
                        location: {
                            ...locObj.location,
                            types: matchedTypes,
                        },
                    };
                })
                .filter(Boolean) as typeof orderedLocations;
            // Sort locations so those with the highest number of unit types whose price range
            // contains the selected investment amount appear first
            validLocations.sort((a, b) => {
                const countMatches = (locObj: any) =>
                    locObj.location.types.filter(type => {
                        const start = parseFloat(type.startingPriceUsd as any) || 0;
                        const end = parseFloat(type.endingPriceUsd as any) || Infinity;
                        return amountDefaultCurrency >= start && amountDefaultCurrency <= end;
                    }).length;

                return countMatches(b) - countMatches(a);
            });

            if (validLocations.length) {
                results[cityName] = validLocations;
            }
        });

        return results;
    }, [groupedLocations, activeUids, amountDefaultCurrency, allowOffPlan]);

    const configuratorSteps = [
        {
            id: 1,
            title: t('steps.filterLocations.title'),
            description: t('steps.filterLocations.description'),
            content: (
                <>
                    <div>
                        {Object.keys(groupedLocations).map(cityName => (
                            <div
                                className={styles.countrySection}
                                key={cityName}
                            >
                                <div className={styles.padding}>
                                    <p
                                        style={{
                                            fontWeight: 500,
                                            marginBottom: '12px',
                                        }}
                                    >
                                        {cityName}
                                    </p>
                                </div>

                                <Swiper
                                    className={styles.swiper}
                                    spaceBetween={20}
                                    keyboard={{ enabled: true }}
                                    mousewheel={{ forceToAxis: true }}
                                    pagination={{ clickable: true }}
                                    modules={[Mousewheel, Keyboard, Pagination]}
                                    breakpoints={{
                                        0: {
                                            slidesPerView: 1.5,
                                        },
                                        768: {
                                            slidesPerView: 3.5,
                                        },
                                    }}
                                >
                                    {groupedLocations[cityName].map(({ location }) => (
                                        <SwiperSlide
                                            className={styles.slide}
                                            key={location?.name}
                                        >
                                            <div className={styles.wrap}>
                                                <div
                                                    className={clsx(styles.location, {
                                                        [styles.active]: activeUids.includes(location?.name),
                                                        [styles.hasImage]: location?.cover?.filename,
                                                    })}
                                                    onClick={() => {
                                                        if (activeUids.includes(location?.name)) {
                                                            setActiveUids(
                                                                activeUids.filter(name => name !== location?.name),
                                                            );
                                                        } else {
                                                            setActiveUids([...activeUids, location?.name]);
                                                        }
                                                        setStepError(null);
                                                    }}
                                                >
                                                    {location?.cover?.filename && (
                                                        <>
                                                            <div className={styles.layer} />
                                                            <Image
                                                                className={styles.cover}
                                                                src={location.cover.filename}
                                                                alt={location.cover.alt}
                                                                fill
                                                                objectFit="cover"
                                                            />
                                                        </>
                                                    )}

                                                    <div className={styles.locationName}>
                                                        <p>{location.name.replace(/\s*\([^)]*\)/g, '')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        ))}
                    </div>

                    <div className={styles.padding}>
                        <div className={styles.selectedLocations}>
                            {Object.entries(groupedLocations).map(([city, locs]) => {
                                const selectedInCity = locs
                                    .filter(({ location }) => activeUids.includes(location.name))
                                    .map(({ location }) => location.name.replace(/\s*\([^)]*\)/g, ''));
                                if (selectedInCity.length === 0) return null;
                                return (
                                    <p
                                        key={city}
                                        className={styles.selectedGroup}
                                    >
                                        <span
                                            style={{
                                                fontWeight: '800',
                                            }}
                                        >
                                            {city}:
                                        </span>{' '}
                                        {selectedInCity.join(', ')}
                                    </p>
                                );
                            })}
                        </div>
                    </div>
                </>
            ),
            actions: (
                <button
                    style={{
                        minWidth: '70%',
                        margin: '0 auto',
                    }}
                    onClick={() => {
                        if (activeUids.length > 0) {
                            setSelectedStep(selectedStep + 1);
                            setStepError(null);
                        } else {
                            setStepError(t('errors.selectLocation'));
                        }
                    }}
                    className={clsx(styles.nextButton)}
                >
                    <ShinyText
                        text={t('buttons.next')}
                        disabled={false}
                        speed={3}
                    />
                </button>
            ),
        },
        {
            id: 2,
            title: t('steps.configureInvestment.title'),
            description: t('steps.configureInvestment.description'),
            content: (
                <div className={styles.padding}>
                    <div className={styles.options}>
                        <div className={styles.currencyWrap}>
                            <p>{t('labels.selectCurrency')}</p>
                            <Suspense fallback={<div style={{ width: '100%', height: 108 }}></div>}>
                                <div>
                                    <CurrencySwitcher className={styles.currency} />
                                </div>
                            </Suspense>
                        </div>
                        <div className={styles.offplanWrap}>
                            <p>{t('labels.enableOffPlan')}</p>
                            <div>
                                <Checkbox
                                    checked={allowOffPlan}
                                    onChange={() => setAllowOffPlan(!allowOffPlan)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.slider}>
                        <ElasticSlider
                            isStepped
                            stepSize={1000}
                            startingValue={smallestStartingPrice}
                            defaultValue={0}
                            maxValue={largestEndingPrice}
                            onValueChange={e => {
                                setValue('investmentAmount', e);
                                setAmountDefaultCurrency(e / currentExchange);
                            }}
                        />
                    </div>
                </div>
            ),
            actions: (
                <>
                    <button
                        className={styles.prevButton}
                        onClick={() => setSelectedStep(selectedStep - 1)}
                    >
                        {t('buttons.previous')}
                    </button>
                    <button
                        onClick={() => {
                            if (investmentAmount) {
                                setSelectedStep(selectedStep + 1);
                                setStepError('');
                            } else {
                                setStepError(t('errors.selectAmount'));
                            }
                        }}
                        className={clsx(styles.nextButton)}
                    >
                        <ShinyText
                            text={t('buttons.next')}
                            disabled={false}
                            speed={3}
                        />
                    </button>
                </>
            ),
        },
        {
            id: 3,
            title: t('steps.contact.title'),
            description: t('steps.contact.description'),
            content: (
                <div className={styles.padding}>
                    <div className={styles.form}>
                        <Controller
                            control={control}
                            rules={{
                                required: t('validation.firstNameRequired'),
                                pattern: {
                                    value: /^[A-Za-z\s]+$/,
                                    message: t('validation.firstNameInvalid'),
                                },
                            }}
                            name="firstname"
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                                <>
                                    <InputFormClient
                                        className={styles.input}
                                        blok={{
                                            label: t('form.firstName'),
                                            width: '50%',
                                            placeholder: t('form.firstNamePlaceholder'),
                                            component: 'inputForm',
                                            _uid: 'any',
                                        }}
                                        props={{ onChange, onBlur, value, ref }}
                                        required
                                        error={error}
                                        isTouched={!!error}
                                    />
                                    {/* {error && <p className={styles.errorMessage}>{error.message}</p>} */}
                                </>
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{
                                required: t('validation.lastNameRequired'),
                                pattern: {
                                    value: /^[A-Za-z\s]+$/,
                                    message: t('validation.lastNameInvalid'),
                                },
                            }}
                            name="lastname"
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                                <InputFormClient
                                    className={styles.input}
                                    blok={{
                                        label: t('form.lastName'),
                                        width: '50%',
                                        placeholder: t('form.lastNamePlaceholder'),
                                        component: 'inputForm',
                                        _uid: 'any',
                                    }}
                                    props={{ onChange, onBlur, value, ref }}
                                    required
                                    error={error}
                                    isTouched={!!error}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{
                                required: t('validation.emailRequired'),
                                pattern: {
                                    value: /^[\w.-]+@[a-zA-Z0-9,-]+\.[a-zA-Z]{2,4}$/,
                                    message: t('validation.emailInvalid'),
                                },
                            }}
                            name="email"
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                                <InputFormClient
                                    className={styles.input}
                                    blok={{
                                        label: t('form.email'),
                                        width: '50%',
                                        placeholder: t('form.emailPlaceholder'),
                                        component: 'inputForm',
                                        _uid: 'any',
                                    }}
                                    props={{ onChange, onBlur, value, ref }}
                                    required
                                    error={error}
                                    isTouched={!!error}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{
                                required: t('validation.phoneRequired'),
                                pattern: {
                                    value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{3,8}$/,
                                    message: t('validation.phoneInvalid'),
                                },
                            }}
                            name="phone"
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                                <InputPhoneClient
                                    className={styles.input}
                                    blok={{
                                        label: t('form.phone'),
                                        width: '50%',
                                        placeholder: t('form.phonePlaceholder'),
                                        component: 'inputForm',
                                        _uid: 'any',
                                    }}
                                    required
                                    props={{ onChange, onBlur, value, ref }}
                                    error={error}
                                    isTouched={!!error}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="company"
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputFormClient
                                    className={styles.input}
                                    blok={{
                                        label: t('form.company'),
                                        width: '50%',
                                        placeholder: t('form.companyPlaceholder'),
                                        component: 'inputForm',
                                        _uid: 'any',
                                    }}
                                    props={{ onChange, onBlur, value, ref }}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    </div>
                </div>
            ),
            actions: (
                <>
                    <button
                        className={styles.prevButton}
                        onClick={() => setSelectedStep(selectedStep - 1)}
                    >
                        {t('buttons.previous')}
                    </button>
                    <button
                        onClick={submitForm}
                        className={clsx(styles.nextButton)}
                    >
                        <ShinyText
                            text={t('buttons.next')}
                            disabled={false}
                            speed={3}
                        />
                    </button>
                </>
            ),
        },
        {
            id: 4,
            title: t('steps.simulate.title'),
            description: t('steps.simulate.description'),
            content: (
                <div className={styles.padding}>
                    <ConfiguratorSimulation
                        results={groupedLocationsResults as any}
                        investmentAmount={amountDefaultCurrency}
                        exchangeRate={currentExchange}
                        currency={currency}
                        setCounterValue={setCounterValue}
                        counterValue={counterValue}
                    />
                </div>
            ),
            actions: (
                <div>
                    <button
                        onClick={() => {
                            reset();
                            setSelectedStep(1);
                        }}
                        className={clsx(styles.backButton)}
                    >
                        {t('buttons.reset')}
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className={clsx(styles.root, className)}>
            {configuratorSteps.map(step => (
                <Accordion
                    key={step.id}
                    title={
                        <div className={styles.padding}>
                            <Typography className="bold">{step.title}</Typography>
                        </div>
                    }
                    className={styles.accordion}
                    isOpen={step.id === selectedStep}
                    controlled
                    icon={
                        <div className={styles.icon}>
                            {selectedStep <= step.id ? (
                                <div
                                    style={{
                                        padding: '6px 10px',
                                        border: '1.5px solid black',
                                        borderRadius: '22px',
                                    }}
                                >
                                    {selectedStep === step.id ? '+' : step.id}
                                </div>
                            ) : (
                                <Icon
                                    style={{
                                        fontSize: '32px',
                                    }}
                                    name="check-circle"
                                />
                            )}
                        </div>
                    }
                >
                    <div className={styles.padding}>
                        <p>{step.description}</p>
                    </div>
                    <div className={styles.content}>{step.content}</div>
                    <div className={styles.padding}>
                        {stepError && (
                            <div
                                style={{
                                    fontSize: '27px',
                                    fontWeight: '700',
                                    color: 'rgba(189, 27, 27, 1) ',
                                }}
                            >
                                <p>{stepError}</p>
                            </div>
                        )}
                        <div className={styles.actions}>{step.actions}</div>
                    </div>
                </Accordion>
            ))}
        </div>
    );
};
